import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AzureResource } from '../models/resource-group';
import { map, Observable } from 'rxjs';
import { DeadLetterMessage } from '../models/dead-letter-message';
import { environment } from 'src/environments/environment';
import { ProcessAllMessagesRequest } from '../models/process-all-messages-request';
import { ProcessMessagesRequest } from '../models/process-messages-request';
import { PlatformPortalApiModelsUserSettings } from '../models/user-settings';
import { YamlConversionRequest } from '../models/yaml-conversion-request';
import { YamlConversionResponse } from '../models/yaml-conversion-response';
import { YamlConversionStatusResponse } from '../models/yaml-conversion-status-response';
import { YamlConversionResultFile } from '../models/yaml-conversion-result-file';
import { RefineYamlConversionRequest } from '../models/refine-yaml-conversion-request';
import { CreateYamlConversionPrRequest } from '../models/create-yaml-conversion-pr-request';
import { CreateYamlConversionPrResponse } from '../models/create-yaml-conversion-pr-response';
import { GetYamlConversionStatusRequest } from '../models/get-yaml-conversion-status-request';
import {
  GitHubTokenRequest,
  GitHubTokenResponse,
} from '../models/github.models';
import { GitHubDeleteBranchRequest } from '../models/github-delete-branch-request';
import { GitHubBranchOperationResult } from '../models/github-branch-operation-result';
import {
  PullRequestFirstReviewRequest,
  PullRequestFirstReviewResponse,
} from '../models/pull-request-first-review';

@Injectable({
  providedIn: 'root',
})
export class PlatformPortalApiService {
  private readonly baseUrl = environment.portalapi;

  constructor(private readonly http: HttpClient) {}

  getGithubToken(request: GitHubTokenRequest): Observable<GitHubTokenResponse> {
    return this.http.post<GitHubTokenResponse>(
      `${this.baseUrl.githubTokenUri}`,
      request,
      {
        observe: 'body',
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  deleteGitHubBranch(request: GitHubDeleteBranchRequest): Observable<GitHubBranchOperationResult> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: request,
    };
    return this.http.delete<GitHubBranchOperationResult>(this.baseUrl.deleteBranchUri, options);
  }

  getServiceBuses(): Observable<AzureResource[]> {
    return this.http.get<AzureResource[]>(`${this.baseUrl.serviceBusesUri}`, {
      observe: 'body',
      headers: { 'Content-Type': 'application/json' },
    });
  }

  getServiceBusQueues(serviceBus: AzureResource): Observable<AzureResource[]> {
    return this.http.post<AzureResource[]>(
      this.baseUrl.serviceBusQueuesUri,
      JSON.stringify(serviceBus),
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'body',
      }
    );
  }

  getDeadLetterMessages(
    queue: AzureResource,
    peekCount: number
  ): Observable<DeadLetterMessage[]> {
    return this.http.post<DeadLetterMessage[]>(
      this.baseUrl.peekDeadLetterUri,
      JSON.stringify(queue),
      {
        observe: 'body',
        params: {
          count: peekCount,
        },
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  processDeadLetterMessage(
    resource: AzureResource,
    messages: string[],
    requeue: boolean
  ): Observable<ProcessMessagesRequest> {
    const request: ProcessMessagesRequest = {
      resource: resource,
      messageIds: messages,
      requeue: requeue,
    };
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: request,
    };

    return this.http.post<ProcessMessagesRequest>(
      this.baseUrl.processMessagesUri,
      request,
      options
    );
  }

  processAllMessages(
    resource: AzureResource,
    requeue: boolean
  ): Observable<string[]> {
    const request: ProcessAllMessagesRequest = {
      resource: resource,
      requeue: requeue,
    };
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: request,
    };

    return this.http.post<string[]>(
      this.baseUrl.processAllMessagesUri,
      request,
      options
    );
  }

  /**
   * Retrieves membership information for a specific organization and file
   * @param org - The organization name (e.g., 'adesacentral')
   * @param fileName - The membership file name to retrieve
   * @returns Observable<T> - The membership response of type T
   */
  getMembership<T>(org: string, fileName: string): Observable<T> {
    const params = new HttpParams().set('org', org).set('fileName', fileName);

    return this.http.get<T>(`${this.baseUrl.membershipUri}/membership`, {
      params,
    });
  }

  getOwnership<T>(org: string, fileName: string): Observable<T> {
    const params = new HttpParams().set('org', org).set('fileName', fileName);
    return this.http.get<T>(`${this.baseUrl.ownershipUri}/ownership`, {
      params,
    });
  }

  /**
   * Retrieves user settings for the current user
   * @returns Observable<PlatformPortalApiModelsUserSettings> - The user settings
   */
  getUserSettings(): Observable<PlatformPortalApiModelsUserSettings> {
    return this.http.get<PlatformPortalApiModelsUserSettings>(
      `${this.baseUrl.userSettingsUri}`,
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'body',
      }
    );
  }

  /**
   * Updates user settings for the current user
   * @param settings - The user settings to update
   * @returns Observable<void> - Empty observable that completes when update is successful
   * @throws Error if server does not return status code 204
   */
  updateUserSettings(
    settings: PlatformPortalApiModelsUserSettings
  ): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl.userSettingsUri}`, settings.settingsJson, {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
      })
      .pipe(
        map((response) => {
          if (response.status !== 204) {
            throw new Error(
              `Expected status code 204, received ${response.status}`
            );
          }
        })
      );
  }

  startConversion(
    request: YamlConversionRequest
  ): Observable<YamlConversionResponse> {
    return this.http.post<YamlConversionResponse>(
      this.baseUrl.startClassicToYamlMigrationUri,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  getConversionStatus(
    request: GetYamlConversionStatusRequest
  ): Observable<YamlConversionStatusResponse> {
    return this.http.post<YamlConversionStatusResponse>(
      this.baseUrl.getClassicToYamlMigrationStatusUri,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  getConversionResult(
    instanceId: string,
    iteration: number
  ): Observable<YamlConversionResultFile[]> {
    const params = new HttpParams()
      .set('instanceId', instanceId)
      .set('iterationNumber', iteration.toString());
    return this.http.get<YamlConversionResultFile[]>(
      this.baseUrl.getClassicToYamlMigrationResultsUri,
      { params }
    );
  }

  refineConversion(
    request: RefineYamlConversionRequest
  ): Observable<YamlConversionResponse> {
    return this.http.post<YamlConversionResponse>(
      this.baseUrl.refineClassicToYamlMigrationUri,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  createPullRequest(
    request: CreateYamlConversionPrRequest
  ): Observable<CreateYamlConversionPrResponse> {
    return this.http.post<CreateYamlConversionPrResponse>(
      this.baseUrl.createPullRequestUri,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  /**
   * Gets non-draft GitHub pull requests created in a period and when each first received a review.
   * Uses the API's GitHub App credentials, so it works without the viewer connecting GitHub.
   * @param request Repositories (and optional owner) plus the period to query
   * @returns Observable<PullRequestFirstReviewResponse>
   */
  getPullRequestFirstReviews(
    request: PullRequestFirstReviewRequest
  ): Observable<PullRequestFirstReviewResponse> {
    return this.http.post<PullRequestFirstReviewResponse>(
      `${this.baseUrl.pullRequestsUri}/first-review`,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }
}
